import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    CircularProgress,
    Box,
    IconButton,
    TextField,
    Snackbar,
    Alert,
    Pagination,
    Grid,
    Card,
    CardContent,
    CardMedia,
    CardActions,
    Chip,
    Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { API_BASE_URL } from '../types';

// Define the type for an unmatched book based on the backend schema
interface UnmatchedBook {
    id: number;
    cover_url: string;
    binding_type: string;
    llm_description: string | null;
    asin: string | null;
    created_at: string; // Assuming string format from backend
    processed: number | null;
}


const UnmatchedBooks: React.FC = () => {
    const { getToken } = useAuth();
    const [unmatchedBooks, setUnmatchedBooks] = useState<UnmatchedBook[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [currentAsin, setCurrentAsin] = useState<string>('');
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [page, setPage] = useState<number>(1);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs()); // Default to today
    const itemsPerPage = 12; // Reduced for card layout (3x4 grid on desktop)

    const fetchUnmatchedBooks = React.useCallback(async (filterDate?: Dayjs | null) => {
        setLoading(true);
        setError(null);
        try {
            const token = await getToken({ template: "covervision-backend" });
             if (!token) {
                throw new Error('Authentication token not available.');
            }

            // Build query parameters
            let queryParams = 'asin_null=true';
            if (filterDate) {
                const formattedDate = filterDate.format('YYYY-MM-DD');
                queryParams += `&created_date=${formattedDate}`;
            }

            const response = await axios.get<UnmatchedBook[]>(`${API_BASE_URL}/unmatched-books/list?${queryParams}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            // Check if the response data is an array before setting the state
            if (Array.isArray(response.data)) {
                setUnmatchedBooks(response.data);
            } else {
                console.error('API response data is not an array:', response.data);
                setError('Received unexpected data format from the server.');
                setUnmatchedBooks([]); // Ensure unmatchedBooks is always an array
                setSnackbarMessage('Received unexpected data format from the server.');
            }
        } catch (err) {
            console.error('Error fetching unmatched books:', err);
            setError('Failed to fetch unmatched books.');
            setSnackbarMessage('Failed to fetch unmatched books.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    }, [getToken]); // Only depend on getToken

    useEffect(() => {
        fetchUnmatchedBooks(selectedDate);
    }, [fetchUnmatchedBooks, selectedDate]); // Re-fetch when date changes

    const handleDateChange = (newDate: Dayjs | null) => {
        setSelectedDate(newDate);
        setPage(1); // Reset to first page when date changes
    };

    const handleEditClick = (book: UnmatchedBook) => {
        setEditingId(book.id);
        setCurrentAsin(book.asin || '');
    };

    const handleSaveClick = async (bookId: number) => {
        if (!currentAsin) {
            setSnackbarMessage('ASIN cannot be empty.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        try {
            const token = await getToken({ template: "covervision-backend" });
            if (!token) {
                throw new Error('Authentication token not available.');
            }

            // Use POST endpoint instead of PATCH to avoid CORS issues
            await axios.post(`${API_BASE_URL}/unmatched-books/${bookId}/update`, {
                asin: currentAsin,
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // Remove the book from the list (since ASIN is no longer null, it won't show in filtered list)
            setUnmatchedBooks(prev => prev.filter(book => book.id !== bookId));

            setSnackbarMessage('ASIN updated successfully. Book scan record also updated.');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setEditingId(null);
            setCurrentAsin('');
        } catch (err) {
            console.error('Error updating ASIN:', err);
            setSnackbarMessage('Failed to update ASIN.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleDeleteClick = async (bookId: number) => {
        if (window.confirm('Are you sure you want to delete this unmatched book?')) {
            try {
                const token = await getToken({ template: "covervision-backend" });
                if (!token) {
                    throw new Error('Authentication token not available.');
                }

                // Use POST endpoint instead of DELETE to avoid CORS issues
                await axios.post(`${API_BASE_URL}/unmatched-books/${bookId}/delete`, {}, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                // Remove the book from the list without reloading
                setUnmatchedBooks(prev => prev.filter(book => book.id !== bookId));

                setSnackbarMessage('Book deleted successfully.');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
            } catch (err) {
                console.error('Error deleting book:', err);
                setSnackbarMessage('Failed to delete book.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Unmatched Books
                </Typography>

                {/* Date Filter */}
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <DatePicker
                                label="Filter by Date"
                                value={selectedDate}
                                onChange={handleDateChange}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        helperText: 'Select a date to view unmatched books'
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item>
                            <Typography variant="body2" color="textSecondary">
                                {unmatchedBooks.length} unmatched book(s) found
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    <Grid container spacing={3}>
                        {unmatchedBooks
                            .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                            .map((book) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={book.id}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'transform 0.2s',
                                            '&:hover': {
                                                transform: 'scale(1.02)',
                                                boxShadow: 6
                                            }
                                        }}
                                    >
                                        {/* Large Cover Image */}
                                        {book.cover_url ? (
                                            <CardMedia
                                                component="img"
                                                image={book.cover_url}
                                                alt={`Cover for ${book.llm_description || 'Unmatched Book'}`}
                                                sx={{
                                                    height: 400,
                                                    objectFit: 'contain',
                                                    backgroundColor: '#f5f5f5',
                                                    padding: 2
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    height: 400,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#f5f5f5'
                                                }}
                                            >
                                                <Typography color="textSecondary">No Cover</Typography>
                                            </Box>
                                        )}

                                        {/* Book Details */}
                                        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                            <Stack spacing={1}>
                                                {/* ID and Binding */}
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    <Chip label={`ID: ${book.id}`} size="small" color="primary" variant="outlined" />
                                                    <Chip label={book.binding_type} size="small" color="secondary" />
                                                </Box>

                                                {/* Description */}
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        minHeight: 40,
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {book.llm_description || 'No description'}
                                                </Typography>

                                                {/* Created Date */}
                                                <Typography variant="caption" color="textSecondary">
                                                    {new Date(book.created_at).toLocaleDateString()}
                                                </Typography>

                                                {/* ASIN Input/Display */}
                                                {editingId === book.id ? (
                                                    <TextField
                                                        value={currentAsin}
                                                        onChange={(e) => setCurrentAsin(e.target.value)}
                                                        size="small"
                                                        variant="outlined"
                                                        placeholder="Enter ASIN"
                                                        autoFocus
                                                        fullWidth
                                                        label="ASIN"
                                                    />
                                                ) : (
                                                    <Box
                                                        onClick={() => handleEditClick(book)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            padding: 1,
                                                            border: '1px dashed',
                                                            borderColor: 'primary.main',
                                                            borderRadius: 1,
                                                            textAlign: 'center',
                                                            '&:hover': {
                                                                backgroundColor: 'action.hover'
                                                            }
                                                        }}
                                                    >
                                                        <Typography variant="body2" color="primary">
                                                            {book.asin || 'Click to add ASIN'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Stack>
                                        </CardContent>

                                        {/* Action Buttons */}
                                        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                                            {editingId === book.id ? (
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleSaveClick(book.id)}
                                                    aria-label="save"
                                                    size="large"
                                                >
                                                    <SaveIcon />
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleEditClick(book)}
                                                    aria-label="edit"
                                                    disabled={loading}
                                                    size="large"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                color="error"
                                                onClick={() => handleDeleteClick(book.id)}
                                                disabled={loading}
                                                aria-label="delete"
                                                size="large"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                    </Grid>
                )}

            {!loading && !error && unmatchedBooks.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                    <Pagination 
                        count={Math.ceil(unmatchedBooks.length / itemsPerPage)} 
                        page={page} 
                        onChange={handlePageChange} 
                        color="primary" 
                    />
                </Box>
            )}

                <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
                    <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Container>
        </LocalizationProvider>
    );
};

export default UnmatchedBooks;
